import React from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import VideoCall from '@mui/icons-material/VideoCall'
import Person from '@mui/icons-material/Person'
import Edit from '@mui/icons-material/Edit'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Upload from '@mui/icons-material/Upload'
import Download from '@mui/icons-material/Download'
import Send from '@mui/icons-material/Send'
import Assessment from '@mui/icons-material/Assessment'
import History from '@mui/icons-material/History'
import Visibility from '@mui/icons-material/Visibility'
import Videocam from '@mui/icons-material/Videocam'
import VideocamOff from '@mui/icons-material/VideocamOff'
import Refresh from '@mui/icons-material/Refresh'
import Schedule from '@mui/icons-material/Schedule'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Description from '@mui/icons-material/Description'
import Payment from '@mui/icons-material/Payment'
import Receipt from '@mui/icons-material/Receipt'
import AccountBalance from '@mui/icons-material/AccountBalance'
import AttachMoney from '@mui/icons-material/AttachMoney'
import { styled } from '@mui/material/styles'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { putCPDataWithoutParams } from 'src/clinic/services'
import {
  BOOKED_EVENT_STEP_STATUSES,
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS_MAP,
  BOOKING_STATUSES,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  SESSION_PLATFORM_ONLINE,
  STATUS_SESSION_BOOKED,
  OFFLINE_PAYMENT_METHODS_LIST,
} from 'src/consumer/constants'
import {
  isCommonResponseSuccessful,
  markBookedSessionAsCompletedFunction,
} from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'

const StyledSubAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  border: '1px solid rgba(67, 56, 202, 0.1)',
  borderRadius: '8px',
  marginBottom: '8px',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 8px 0',
  },
}))

const SubAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  minHeight: '48px',
  padding: '0 12px',
  '&.Mui-expanded': {
    minHeight: '48px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
    alignItems: 'center',
  },
}))

const SubAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}))

const ActionButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: '1px solid rgba(67, 56, 202, 0.1)',
  background: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    background: 'rgba(67, 56, 202, 0.05)',
    borderColor: 'rgba(67, 56, 202, 0.2)',
    transform: 'translateX(4px)',
  },
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: 'rgba(67, 56, 202, 0.8)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}))

const ActionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.87)',
  flex: 1,
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  background: 'rgba(67, 56, 202, 0.02)',
  borderRadius: '8px',
  borderLeft: '3px solid rgba(67, 56, 202, 0.2)',
}))

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  minWidth: '140px',
}))

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.85)',
  flex: 1,
}))

export default function BookedSlotActionsAccordion({
  bookedSlot,
  forMentor,
  setClickedBookedSlotData,
  setEditPatientDialogOpen,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  handleNewTherapyRequest,
  therapyBookingDataForThisEvent,
  refreshBookedEvents,
  markBookedSessionAsCompletedFunction,
  handleOpenJoiningDetails,
  openUploadPrescriptionImagesDialog,
  openBookedEventLogs,
  openEmrImagesDialog,
  openSendAssessmentDialog,
  openPatientBookingDialog,
  openSendTherapyPopover,
  handleToggleRecording,
  showPrescriptionPdfDownloadButtons = false,
  openPrescriptionPdfDownloadDialog,
  forceShowJoiningDetailsActionButton,
  openRescheduleDialog,
  hideRescheduleDialog,
  showViewReportsButtonInActionButtons = false,
  openReportViewDrawer,
  openPaymentLinksDialog,
  isMainAccordionExpanded = false,
  openReceiptDialog,
  setEventDataForReceiptPrinting,
}) {
  
  const roles = getAllRolesInBoolean()
  const isRecordingEnabled = bookedSlot?.recordingEnabled
  const isBookedSlotBlockedForPayment =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
  
  // Detect if we're in support portal (check for both /support and /support-home)
  const isSupportPortal = typeof window !== 'undefined' && 
    (window.location?.pathname?.includes('/support') || window.location?.pathname?.includes('/support-home'))

  // Joining Details should show if:
  // 1. Slot is online AND
  // 2. Not blocked for payment AND
  // 3. (User is mentor OR forceShowJoiningDetailsActionButton is true OR in support portal)
  // For support portal, show joining details for ALL online sessions
  // The handler availability is checked separately when rendering the button
  const showJoiningDetails =
    bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE &&
    !isBookedSlotBlockedForPayment &&
    (forMentor || forceShowJoiningDetailsActionButton || isSupportPortal)
  
  // For support portal, always show joining details section if slot is online
  // (even if handler isn't available, we'll show a message)
  const shouldShowJoiningDetailsInSupportPortal = 
    isSupportPortal && 
    bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE &&
    !isBookedSlotBlockedForPayment

  // Helper function to safely execute actions
  const safeAction = (action, actionName) => {
    return () => {
      try {
        if (action && typeof action === 'function') {
          action()
        }
      } catch (error) {
        console.error(`Error executing ${actionName}:`, error)
        toast.error(`Error: ${actionName}`)
      }
    }
  }

  async function bookScreeningForThisBooking() {
    try {
      const response = await putCPDataWithoutParams(`/api/secure/meeting/screening/book`, {
        ...bookedSlot,
        apiParam: 'booked-event-info',
      })
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        response?.data?.data?.screening
      ) {
        toast.success(
          `Screening booked for ${response?.data?.data?.screening?.userName} with ${response?.data?.data?.screening?.mentorName}`,
        )
        if (refreshBookedEvents) refreshBookedEvents()
      } else {
        toast.error('Error booking screening')
      }
    } catch (error) {
      console.error('Error booking screening:', error)
      toast.error('Error booking screening')
    }
  }

  async function raiseReassignScreeningsRequest() {
    try {
      const response = await putCPDataWithoutParams(
        `/api/secure/meeting/screening/auto-reassign-eligible`,
        {
          ...bookedSlot,
          apiParam: 'booked-event-info',
        },
      )
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        toast.success('Requested')
        if (refreshBookedEvents) refreshBookedEvents()
      } else {
        toast.error('Error')
      }
    } catch (error) {
      console.error('Error requesting reassign:', error)
      toast.error('Error')
    }
  }

  // Only render if main accordion is expanded
  if (!isMainAccordionExpanded) {
    return null
  }

  // Determine if Session & Joining section should show
  // Section shows if: joining details available OR online meeting OR support roles OR support portal with online slot
  const shouldShowSessionJoiningSection =
    showJoiningDetails ||
    shouldShowJoiningDetailsInSupportPortal ||
    bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE ||
    roles.isL1TherapySupport ||
    roles.isL2TherapySupport ||
    roles.isL3TherapySupport

  return (
    <Box sx={{ mt: 2 }}>
      {/* Session & Joining Section */}
      {shouldShowSessionJoiningSection && (
        <StyledSubAccordion>
          <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
            <SectionTitle>
              <VideoCall sx={{ fontSize: 18 }} />
              Session & Joining
            </SectionTitle>
          </SubAccordionSummary>
          <SubAccordionDetails>
            {(showJoiningDetails || shouldShowJoiningDetailsInSupportPortal) && handleOpenJoiningDetails && (
              <ActionButton onClick={safeAction(handleOpenJoiningDetails, 'open joining details')}>
                <VideoCall sx={{ fontSize: 20, color: '#4338ca' }} />
                <ActionLabel>Joining Details</ActionLabel>
              </ActionButton>
            )}
            {(showJoiningDetails || shouldShowJoiningDetailsInSupportPortal) && !handleOpenJoiningDetails && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontStyle: 'italic',
                  padding: '8px',
                }}
              >
                Joining details handler not available
              </Typography>
            )}
            {(roles.isL1TherapySupport ||
              roles.isL2TherapySupport ||
              roles.isL3TherapySupport) &&
              openBookedEventLogs && (
                <ActionButton onClick={safeAction(openBookedEventLogs, 'open event logs')}>
                  <Description sx={{ fontSize: 20, color: 'rgba(0, 0, 0, 0.6)' }} />
                  <ActionLabel>Event Logs</ActionLabel>
                </ActionButton>
              )}
            {!showJoiningDetails &&
              !(roles.isL1TherapySupport ||
                roles.isL2TherapySupport ||
                roles.isL3TherapySupport) &&
              bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.5)',
                    fontStyle: 'italic',
                    padding: '8px',
                  }}
                >
                  Joining details not available in this context
                </Typography>
              )}
          </SubAccordionDetails>
        </StyledSubAccordion>
      )}

      {/* Reports & Documents Section */}
      {(showViewReportsButtonInActionButtons ||
        showPrescriptionPdfDownloadButtons ||
        (forMentor && bookedSlot?.eventType === EVENT_TYPE_CONSULTATION) ||
        (forMentor &&
          (bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION ||
            bookedSlot?.eventType === EVENT_TYPE_SCREENING))) && (
        <StyledSubAccordion>
          <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
            <SectionTitle>
              <Description sx={{ fontSize: 18 }} />
              Reports & Documents
            </SectionTitle>
          </SubAccordionSummary>
          <SubAccordionDetails>
            {showViewReportsButtonInActionButtons && openReportViewDrawer && (
              <ActionButton
                onClick={safeAction(() => openReportViewDrawer(bookedSlot), 'open report view')}
              >
                <Visibility sx={{ fontSize: 20, color: '#4338ca' }} />
                <ActionLabel>View Report</ActionLabel>
              </ActionButton>
            )}
            {showPrescriptionPdfDownloadButtons && openPrescriptionPdfDownloadDialog && (
              <ActionButton
                onClick={safeAction(openPrescriptionPdfDownloadDialog, 'open prescription download')}
              >
                <Download sx={{ fontSize: 20, color: '#10b981' }} />
                <ActionLabel>Download PDF</ActionLabel>
              </ActionButton>
            )}
            {forMentor && bookedSlot?.eventType === EVENT_TYPE_CONSULTATION && openUploadPrescriptionImagesDialog && (
              <ActionButton
                onClick={safeAction(openUploadPrescriptionImagesDialog, 'open upload prescription')}
              >
                <Upload sx={{ fontSize: 20, color: '#f59e0b' }} />
                <ActionLabel>Upload Prescription</ActionLabel>
              </ActionButton>
            )}
            {forMentor &&
              (bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION ||
                bookedSlot?.eventType === EVENT_TYPE_SCREENING ||
                bookedSlot?.eventType === 'assessment' ||
                bookedSlot?.eventType === 'assessment-report' ||
                bookedSlot?.eventType === 'investigation' ||
                bookedSlot?.eventType === 'investigation-report') &&
              openEmrImagesDialog && (() => {
                const getEmrButtonLabel = () => {
                  const eventType = bookedSlot?.eventType
                  if (eventType === EVENT_TYPE_THERAPY_SESSION) return 'Upload Therapy Report Images'
                  if (eventType === EVENT_TYPE_SCREENING) return 'Upload Screening Report Images'
                  if (eventType === 'assessment' || eventType === 'assessment-report') return 'Upload Assessment Report Images'
                  if (eventType === 'investigation' || eventType === 'investigation-report') return 'Upload Investigation Report Images'
                  return 'Upload EMR Report Images'
                }
                return (
                  <ActionButton onClick={safeAction(openEmrImagesDialog, 'open EMR images upload')}>
                    <Upload sx={{ fontSize: 20, color: '#8b5cf6' }} />
                    <ActionLabel>{getEmrButtonLabel()}</ActionLabel>
                  </ActionButton>
                )
              })()}
            {forMentor &&
              (bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION ||
                bookedSlot?.eventType === EVENT_TYPE_SCREENING ||
                bookedSlot?.eventType === 'assessment' ||
                bookedSlot?.eventType === 'assessment-report' ||
                bookedSlot?.eventType === 'investigation' ||
                bookedSlot?.eventType === 'investigation-report') && (
                <ActionButton onClick={() => {}}>
                  <Send sx={{ fontSize: 20, color: '#8b5cf6' }} />
                  <ActionLabel>Send Report to Doctor</ActionLabel>
                </ActionButton>
              )}
          </SubAccordionDetails>
        </StyledSubAccordion>
      )}

      {/* Actions & Referrals Section */}
      {((!Boolean(bookedSlot?.previousSteps) || bookedSlot?.previousSteps?.length === 0) &&
        !forMentor) ||
      (forMentor && roles.isDoctor) ||
      !forMentor ? (
        <StyledSubAccordion>
          <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
            <SectionTitle>
              <LocalHospital sx={{ fontSize: 18 }} />
              Actions & Referrals
            </SectionTitle>
          </SubAccordionSummary>
          <SubAccordionDetails>
            {(!Boolean(bookedSlot?.previousSteps) ||
              bookedSlot?.previousSteps?.length === 0) &&
              !forMentor && (
                <ActionButton onClick={safeAction(bookScreeningForThisBooking, 'book screening')}>
                  <LocalHospital sx={{ fontSize: 20, color: '#ec4899' }} />
                  <ActionLabel>Book Screening</ActionLabel>
                </ActionButton>
              )}
            {forMentor && roles.isDoctor && openSendTherapyPopover && (
              <ActionButton onClick={safeAction(openSendTherapyPopover, 'send for therapy')}>
                <Send sx={{ fontSize: 20, color: '#8b5cf6' }} />
                <ActionLabel>Send for Therapy</ActionLabel>
              </ActionButton>
            )}
            {forMentor && roles.isDoctor && openSendAssessmentDialog && (
              <ActionButton onClick={safeAction(openSendAssessmentDialog, 'send for assessment')}>
                <Assessment sx={{ fontSize: 20, color: '#6366f1' }} />
                <ActionLabel>Send for Assessment</ActionLabel>
              </ActionButton>
            )}
            {forMentor && roles.isDoctor && openPatientBookingDialog && (
              <ActionButton onClick={safeAction(openPatientBookingDialog, 'open treatment history')}>
                <History sx={{ fontSize: 20, color: '#10b981' }} />
                <ActionLabel>Treatment History</ActionLabel>
              </ActionButton>
            )}
            {!forMentor && handleNewTherapyRequest && (
              <ActionButton
                onClick={safeAction(
                  () => handleNewTherapyRequest(therapyBookingDataForThisEvent, bookedSlot),
                  'book therapy',
                )}
              >
                <LocalHospital sx={{ fontSize: 20, color: '#ec4899' }} />
                <ActionLabel>Book Therapy</ActionLabel>
              </ActionButton>
            )}
            {Boolean(bookedSlot?.previousSteps) &&
              bookedSlot?.previousSteps?.length === 1 &&
              bookedSlot?.previousSteps?.[0]?.status ===
                BOOKED_EVENT_STEP_STATUSES.HANDLER_NOT_ASSIGNED && (
                <ActionButton
                  onClick={safeAction(raiseReassignScreeningsRequest, 'request reassign')}
                >
                  <Refresh sx={{ fontSize: 20, color: '#f59e0b' }} />
                  <ActionLabel>Request Reassign</ActionLabel>
                </ActionButton>
              )}
          </SubAccordionDetails>
        </StyledSubAccordion>
      ) : null}

      {/* Recording Section */}
      {(roles.isL1TherapySupport ||
        roles.isL2TherapySupport ||
        roles.isL3TherapySupport) &&
        bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS_MAP['not-started']?.id &&
        handleToggleRecording && (
          <StyledSubAccordion>
            <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
              <SectionTitle>
                {isRecordingEnabled ? (
                  <VideocamOff sx={{ fontSize: 18 }} />
                ) : (
                  <Videocam sx={{ fontSize: 18 }} />
                )}
                Recording
              </SectionTitle>
            </SubAccordionSummary>
            <SubAccordionDetails>
              <ActionButton onClick={safeAction(handleToggleRecording, 'toggle recording')}>
                {isRecordingEnabled ? (
                  <VideocamOff sx={{ fontSize: 20, color: '#dc2626' }} />
                ) : (
                  <Videocam sx={{ fontSize: 20, color: '#10b981' }} />
                )}
                <ActionLabel>
                  {isRecordingEnabled ? 'Disable Recording' : 'Enable Recording'}
                </ActionLabel>
              </ActionButton>
            </SubAccordionDetails>
          </StyledSubAccordion>
        )}

      {/* Payment Links Section - Show when payment status != paid, or when blocked for payment, or when payment links exist */}
      {/* In support portal, show for all slots where payment status is not PAID */}
      {(() => {
        const hasPaymentStatus = bookedSlot?.paymentStatus !== undefined && bookedSlot?.paymentStatus !== null
        const isNotPaid = hasPaymentStatus && bookedSlot.paymentStatus !== BOOKING_PAYMENT_STATUSES.PAID
        // In support portal, show if payment status is not PAID (or undefined)
        const shouldShowForSupportPortal = isSupportPortal && (!hasPaymentStatus || isNotPaid)
        const hasPaymentLinks = bookedSlot?.paymentLinks && Array.isArray(bookedSlot.paymentLinks) && bookedSlot.paymentLinks.length > 0
        
        // Show section if conditions are met (handler availability checked inside)
        return isNotPaid || shouldShowForSupportPortal || isBookedSlotBlockedForPayment || hasPaymentLinks
      })() && (
          <StyledSubAccordion>
            <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
              <SectionTitle>
                <Payment sx={{ fontSize: 18 }} />
                Payment Links
              </SectionTitle>
            </SubAccordionSummary>
            <SubAccordionDetails>
              {openPaymentLinksDialog ? (
                <ActionButton onClick={safeAction(openPaymentLinksDialog, 'open payment links')}>
                  <Payment sx={{ fontSize: 20, color: '#4338ca' }} />
                  <ActionLabel>View Payment Links</ActionLabel>
                </ActionButton>
              ) : (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.5)',
                    fontStyle: 'italic',
                    padding: '8px',
                  }}
                >
                  Payment links handler not available
                </Typography>
              )}
              {bookedSlot?.paymentLinks && Array.isArray(bookedSlot.paymentLinks) && bookedSlot.paymentLinks.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    marginTop: '8px',
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontSize: '12px',
                    paddingLeft: '16px',
                  }}
                >
                  {bookedSlot.paymentLinks.length} payment link{bookedSlot.paymentLinks.length !== 1 ? 's' : ''} available
                </Typography>
              )}
            </SubAccordionDetails>
          </StyledSubAccordion>
        )}

      {/* Fee & Payment Details Section */}
      {/* Show this section if there's any payment-related data in the bookedSlot */}
      {/* Based on actual API response structure from booked-slots/all-with-patient-info */}
      {/* This section appears for both therapist and doctor routes since they use the same component */}
      {(() => {
        if (!bookedSlot) {
          return null
        }
        return (
          <StyledSubAccordion>
            <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
              <SectionTitle>
                <Receipt sx={{ fontSize: 18 }} />
                Fee & Payment Details
              </SectionTitle>
            </SubAccordionSummary>
            <SubAccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Basic Payment Information */}
                {(bookedSlot?.price !== undefined || bookedSlot?.finalPrice !== undefined || bookedSlot?.feePaid !== undefined) && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Payment Amounts
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {bookedSlot?.price !== undefined && bookedSlot?.price !== null && (
                        <InfoRow>
                          <InfoLabel>Price</InfoLabel>
                          <InfoValue>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.price.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.discount !== undefined && bookedSlot?.discount !== null && bookedSlot.discount > 0 && (
                        <InfoRow>
                          <InfoLabel>Discount</InfoLabel>
                          <InfoValue>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.discount.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.finalPrice !== undefined && bookedSlot?.finalPrice !== null && (
                        <InfoRow>
                          <InfoLabel>Final Price</InfoLabel>
                          <InfoValue sx={{ fontWeight: 600, color: '#4338ca' }}>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.finalPrice.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.feePaid !== undefined && bookedSlot?.feePaid !== null && (
                        <InfoRow>
                          <InfoLabel>Fee Paid</InfoLabel>
                          <InfoValue>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.feePaid.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Payment Method & Transaction Details */}
                {(bookedSlot?.feePaymentMode || bookedSlot?.feePaidTxnId || bookedSlot?.orderId || bookedSlot?.txnId || bookedSlot?.feeCollectionDetails?.orderId) && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Transaction Details
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {bookedSlot?.feePaymentMode && (
                        <InfoRow>
                          <InfoLabel>Payment Mode</InfoLabel>
                          <InfoValue>
                            {OFFLINE_PAYMENT_METHODS_LIST?.find(m => m.id === bookedSlot.feePaymentMode)?.title || bookedSlot.feePaymentMode}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {/* Order ID can be at top level or inside feeCollectionDetails */}
                      {(bookedSlot?.orderId || bookedSlot?.feeCollectionDetails?.orderId) && (
                        <InfoRow>
                          <InfoLabel>Order ID</InfoLabel>
                          <InfoValue sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {bookedSlot?.orderId || bookedSlot?.feeCollectionDetails?.orderId}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.txnId && (
                        <InfoRow>
                          <InfoLabel>Transaction ID</InfoLabel>
                          <InfoValue sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{bookedSlot.txnId}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.feePaidTxnId && (
                        <InfoRow>
                          <InfoLabel>Fee Transaction ID</InfoLabel>
                          <InfoValue sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{bookedSlot.feePaidTxnId}</InfoValue>
                        </InfoRow>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Print Receipt / Invoice */}
                {openReceiptDialog && setEventDataForReceiptPrinting && (
                  <ActionButton
                    onClick={safeAction(() => {
                      setEventDataForReceiptPrinting({ eventData: bookedSlot, eventId: bookedSlot?.id })
                      openReceiptDialog()
                    }, 'Open receipt')}
                  >
                    <Receipt sx={{ fontSize: 20, color: 'rgba(67, 56, 202, 0.8)' }} />
                    <ActionLabel>Invoice / Print Receipt</ActionLabel>
                  </ActionButton>
                )}

                {/* Additional Information */}
                {(bookedSlot?.subscriptionPlanId || bookedSlot?.purchase || bookedSlot?.env || bookedSlot?.currency) && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Additional Information
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {bookedSlot?.subscriptionPlanId && (
                        <InfoRow>
                          <InfoLabel>Subscription Plan ID</InfoLabel>
                          <InfoValue sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{bookedSlot.subscriptionPlanId}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.purchase && (
                        <InfoRow>
                          <InfoLabel>Purchase</InfoLabel>
                          <InfoValue>{bookedSlot.purchase}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.env && (
                        <InfoRow>
                          <InfoLabel>Environment</InfoLabel>
                          <InfoValue>{bookedSlot.env}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot?.currency && (
                        <InfoRow>
                          <InfoLabel>Currency</InfoLabel>
                          <InfoValue>{bookedSlot.currency.toUpperCase()}</InfoValue>
                        </InfoRow>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Cash Breakup */}
                {bookedSlot?.cashBreakUp && typeof bookedSlot.cashBreakUp === 'object' && Object.keys(bookedSlot.cashBreakUp).length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Cash Breakup
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {Object.entries(bookedSlot.cashBreakUp).map(([denomination, count]) => (
                        <InfoRow key={denomination}>
                          <InfoLabel>₹{denomination}</InfoLabel>
                          <InfoValue>{count} × ₹{denomination} = ₹{(parseInt(denomination) * count).toFixed(2)}</InfoValue>
                        </InfoRow>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Fee Collection Details */}
                {bookedSlot?.feeCollectionDetails && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Fee Collection Details
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {bookedSlot.feeCollectionDetails?.collectedBy && (
                        <InfoRow>
                          <InfoLabel>Collected By</InfoLabel>
                          <InfoValue>{bookedSlot.feeCollectionDetails.collectedBy}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.feeCollectionDetails?.orderId && (
                        <InfoRow>
                          <InfoLabel>Collection Order ID</InfoLabel>
                          <InfoValue sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{bookedSlot.feeCollectionDetails.orderId}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.feeCollectionDetails?.paymentGateway && (
                        <InfoRow>
                          <InfoLabel>Payment Gateway</InfoLabel>
                          <InfoValue>{bookedSlot.feeCollectionDetails.paymentGateway}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.feeCollectionDetails?.actualFeeCollected !== undefined && bookedSlot.feeCollectionDetails?.actualFeeCollected !== null && (
                        <InfoRow>
                          <InfoLabel>Actual Fee Collected</InfoLabel>
                          <InfoValue sx={{ fontWeight: 600, color: '#10b981' }}>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.feeCollectionDetails.actualFeeCollected.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.feeCollectionDetails?.collectedOn && (
                        <InfoRow>
                          <InfoLabel>Collected On</InfoLabel>
                          <InfoValue>
                            {dayjs.utc(bookedSlot.feeCollectionDetails.collectedOn).local().format('DD MMM YYYY, h:mm A')}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.feeCollectionDetails?.notes && (
                        <InfoRow>
                          <InfoLabel>Notes</InfoLabel>
                          <InfoValue>{bookedSlot.feeCollectionDetails.notes}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.feeCollectionDetails?.intervention !== undefined && (
                        <InfoRow>
                          <InfoLabel>Intervention</InfoLabel>
                          <InfoValue>{bookedSlot.feeCollectionDetails.intervention ? 'Yes' : 'No'}</InfoValue>
                        </InfoRow>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Reconciliation Adjustment */}
                {bookedSlot?.reconciliationAdjustment && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Reconciliation Adjustment
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {bookedSlot.reconciliationAdjustment?.correctedFeeCollected !== undefined && bookedSlot.reconciliationAdjustment?.correctedFeeCollected !== null && (
                        <InfoRow>
                          <InfoLabel>Corrected Fee Collected</InfoLabel>
                          <InfoValue sx={{ fontWeight: 600, color: '#f59e0b' }}>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.reconciliationAdjustment.correctedFeeCollected.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.reconciliationAdjustment?.adjustmentAmount !== undefined && bookedSlot.reconciliationAdjustment?.adjustmentAmount !== null && (
                        <InfoRow>
                          <InfoLabel>Adjustment Amount</InfoLabel>
                          <InfoValue sx={{ color: bookedSlot.reconciliationAdjustment.adjustmentAmount >= 0 ? '#10b981' : '#ef4444' }}>
                            {(bookedSlot?.currency || 'INR').toUpperCase()} {bookedSlot.reconciliationAdjustment.adjustmentAmount >= 0 ? '+' : ''}{bookedSlot.reconciliationAdjustment.adjustmentAmount.toFixed(2)}
                          </InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.reconciliationAdjustment?.note && (
                        <InfoRow>
                          <InfoLabel>Adjustment Note</InfoLabel>
                          <InfoValue>{bookedSlot.reconciliationAdjustment.note}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.reconciliationAdjustment?.appliedBy && (
                        <InfoRow>
                          <InfoLabel>Applied By</InfoLabel>
                          <InfoValue>{bookedSlot.reconciliationAdjustment.appliedBy}</InfoValue>
                        </InfoRow>
                      )}
                      {bookedSlot.reconciliationAdjustment?.appliedAt && (
                        <InfoRow>
                          <InfoLabel>Applied At</InfoLabel>
                          <InfoValue>
                            {dayjs.utc(bookedSlot.reconciliationAdjustment.appliedAt).local().format('DD MMM YYYY, h:mm A')}
                          </InfoValue>
                        </InfoRow>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Fee Collection Note */}
                {bookedSlot?.feeCollectionNote && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                      Collection Note
                    </Typography>
                    <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'rgba(67, 56, 202, 0.05)', borderRadius: '8px', borderLeft: '3px solid rgba(67, 56, 202, 0.3)' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.85)', whiteSpace: 'pre-wrap' }}>
                        {bookedSlot.feeCollectionNote}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </SubAccordionDetails>
          </StyledSubAccordion>
        )
      })()}

      {/* Booking Management Section */}
      <StyledSubAccordion>
        <SubAccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}>
          <SectionTitle>
            <Edit sx={{ fontSize: 18 }} />
            Booking Management
          </SectionTitle>
        </SubAccordionSummary>
        <SubAccordionDetails>
          {/* Mark Completed - Only show for booked status */}
          {forMentor && 
           !bookedSlot?.markedCompleted && 
           !isBookedSlotBlockedForPayment && 
           (bookedSlot?.status === STATUS_SESSION_BOOKED || bookedSlot?.status === BOOKING_STATUSES.BOOKED) && (
            <ActionButton
              onClick={safeAction(async () => {
                await markBookedSessionAsCompletedFunction(bookedSlot)
                if (refreshBookedEvents) refreshBookedEvents()
              }, 'mark completed')}
            >
              <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
              <ActionLabel>Mark Completed</ActionLabel>
            </ActionButton>
          )}
          <ActionButton
            onClick={safeAction(() => {
              if (setClickedBookedSlotData) setClickedBookedSlotData(bookedSlot)
              if (setEditPatientDialogOpen) setEditPatientDialogOpen(true)
            }, 'open patient details')}
          >
            <Person sx={{ fontSize: 20, color: '#6366f1' }} />
            <ActionLabel>Patient Details</ActionLabel>
          </ActionButton>
          {!isBookedSlotBlockedForPayment && (
            <ActionButton
              onClick={safeAction(() => {
                if (setClickedBookedSlotData) setClickedBookedSlotData(bookedSlot)
                if (setAddVitalsDialogOpen) setAddVitalsDialogOpen(true)
              }, 'add vitals')}
            >
              <Description sx={{ fontSize: 20, color: '#8b5cf6' }} />
              <ActionLabel>Add Vitals</ActionLabel>
            </ActionButton>
          )}
          <ActionButton
            onClick={safeAction(() => {
              if (setClickedBookedSlotData) setClickedBookedSlotData(bookedSlot)
              if (setEditBookingDialogOpen) setEditBookingDialogOpen(true)
            }, 'edit booking')}
          >
            <Edit sx={{ fontSize: 20, color: '#4338ca' }} />
            <ActionLabel>Edit Booking</ActionLabel>
          </ActionButton>
          {openRescheduleDialog &&
            !isBookedSlotBlockedForPayment &&
            !bookedSlot?.markedCompleted &&
            !hideRescheduleDialog && (
              <ActionButton onClick={safeAction(openRescheduleDialog, 'reschedule')}>
                <Schedule sx={{ fontSize: 20, color: '#f59e0b' }} />
                <ActionLabel>Reschedule</ActionLabel>
              </ActionButton>
            )}
        </SubAccordionDetails>
      </StyledSubAccordion>
    </Box>
  )
}

